/**
 * Pure view model for the Results tab's Picks view (ISSUE-1317): who picked
 * what in one round, the web twin of the legacy Pick History tab.
 *
 * `POST /results/get-by-round` answers per game, and inside each game only
 * the picks that have locked (SUR-R28). This module flattens that into one
 * row per pick, the way the legacy list reads ("Name, team, vs opponent,
 * result"), and applies the two filters a fan can set. It never decides a
 * result: `outcome` is the server's word for every pick.
 *
 * Once a round locks, the server also names who made no pick in it
 * (`nonPickers`, ISSUE-1317). Each one becomes a "Missed, No pick made" or a
 * "Skipped" row, like the legacy list. A miss counts under Losses, as it did
 * there; a declared skip shows only under All.
 *
 * The player filter is v1's multi-select member sheet (PoolMemberSelectionSheet)
 * as a row of chips, the same as mobile: the viewer first as "You", then
 * everyone by name. Any set of picked players narrows the rows to just them.
 * The search narrows the chips but never drops a picked one, and only while
 * nobody is picked does it also narrow the rows by name.
 *
 * No React, no network, no SDK value import.
 */
import { survivorRoundPickResultLabel } from "./SurvivorRoundList";

export const ROUND_PICKS_LOADING_LABEL = "Loading picks";
export const ROUND_PICKS_FAILED_MESSAGE = "We could not load the picks.";
export const ROUND_PICKS_RETRY_LABEL = "Try again";
export const ROUND_PICKS_ROUND_LABEL = "Round";
export const ROUND_PICKS_MEMBER_LABEL = "Player";
export const ROUND_PICKS_EVERYONE_LABEL = "Everyone";
export const ROUND_PICKS_YOU_LABEL = "You";
export const ROUND_PICKS_MEMBER_SEARCH_LABEL = "Search players";
export const ROUND_PICKS_MEMBER_GROUP_LABEL = "Filter picks by player";
export const ROUND_PICKS_OUTCOME_GROUP_LABEL = "Filter picks by result";
export const ROUND_PICKS_NO_ROUNDS_LINE =
  "Rounds show up here once the contest has them.";
export const ROUND_PICKS_NONE_LOCKED_LINE =
  "No picks are public in this round yet. Each pick shows once it locks.";
export const ROUND_PICKS_FILTERED_EMPTY_LINE = "No picks match these filters.";
export const ROUND_PICKS_YOU_TAG = "(you)";
export const ROUND_PICKS_MISSED_LABEL = "Missed";
export const ROUND_PICKS_MISSED_DETAIL = "No pick made";
export const ROUND_PICKS_SKIPPED_LABEL = "Skipped";
export const ROUND_PICKS_SKIPPED_DETAIL = "Sat this round out";
export const ROUND_PICKS_CORRECTED_LINE =
  "The Commissioner corrected this round after it was graded.";

export type typeRoundPicksOutcomeFilter = "all" | "win" | "loss" | "pending";

export const ROUND_PICKS_OUTCOME_FILTERS: readonly {
  value: typeRoundPicksOutcomeFilter;
  label: string;
}[] = [
  { value: "all", label: "All" },
  { value: "win", label: "Wins" },
  { value: "loss", label: "Losses" },
  { value: "pending", label: "Pending" },
];

export type typeRoundPickOutcome = "win" | "loss" | "push" | "pending";

export type typeRoundPicksWireGame = {
  eventId: string;
  homeTeamId: string;
  awayTeamId: string;
  picks: readonly {
    entryId: string;
    displayName: string;
    teamId: string;
    outcome: typeRoundPickOutcome;
    decisionBasis?: "spread" | "straightUp" | null;
    isViewer: boolean;
  }[];
};

export type typeRoundNonPicker = {
  entryId: string;
  displayName: string;
  isViewer: boolean;
  participation: "missed" | "skipped";
};

export type typeRoundPickRow =
  | {
      key: string;
      participation: "picked";
      entryId: string;
      displayName: string;
      isViewer: boolean;
      teamId: string;
      teamName: string;
      opponentName: string;
      outcome: typeRoundPickOutcome;
      outcomeLabel: string;
    }
  | {
      key: string;
      participation: "missed" | "skipped";
      entryId: string;
      displayName: string;
      isViewer: boolean;
      /** "Missed" or "Skipped", in the team name's place. */
      label: string;
      /** "No pick made" or "Sat this round out", in the opponent's place. */
      detail: string;
    };

export type typeRoundOption = {
  roundId: string;
  label: string;
  locked: boolean;
};

export type typeRoundPicksMemberChoice = {
  entryId: string;
  label: string;
  isSelected: boolean;
};

type typeTeamLookup = ReadonlyMap<string, { name: string }>;

function teamNameFor(teamId: string, teamsById: typeTeamLookup): string {
  const name = teamsById.get(teamId)?.name;
  return name === undefined || name === "" ? teamId : name;
}

export function opponentLine(opponentName: string): string {
  return `vs ${opponentName}`;
}

/**
 * The round the Picks view opens on: the latest round that has locked, since
 * that is the newest round with picks anyone can see. With nothing locked yet
 * it is the first round, which then shows the "nothing public yet" line.
 */
export function defaultRoundId(
  rounds: readonly typeRoundOption[],
): string | null {
  for (let index = rounds.length - 1; index >= 0; index -= 1) {
    const round = rounds[index];
    if (round?.locked === true) return round.roundId;
  }
  return rounds[0]?.roundId ?? null;
}

/** A requested round that is not in the list falls back to the default. */
export function resolveRoundId(
  rounds: readonly typeRoundOption[],
  requested: string | null,
): string | null {
  if (requested !== null && rounds.some((r) => r.roundId === requested)) {
    return requested;
  }
  return defaultRoundId(rounds);
}

/**
 * One row per locked pick plus one per entry that made no pick, sorted by
 * name so a fan can scan for a friend.
 */
export function buildRoundPickRows(
  games: readonly typeRoundPicksWireGame[],
  teamsById: typeTeamLookup,
  nonPickers: readonly typeRoundNonPicker[] = [],
): typeRoundPickRow[] {
  const rows: typeRoundPickRow[] = [];
  for (const nonPicker of nonPickers) {
    const isMissed = nonPicker.participation === "missed";
    rows.push({
      key: `none-${nonPicker.entryId}`,
      participation: nonPicker.participation,
      entryId: nonPicker.entryId,
      displayName: nonPicker.displayName,
      isViewer: nonPicker.isViewer,
      label: isMissed ? ROUND_PICKS_MISSED_LABEL : ROUND_PICKS_SKIPPED_LABEL,
      detail: isMissed ? ROUND_PICKS_MISSED_DETAIL : ROUND_PICKS_SKIPPED_DETAIL,
    });
  }
  for (const game of games) {
    for (const pick of game.picks) {
      const opponentId =
        pick.teamId === game.homeTeamId ? game.awayTeamId : game.homeTeamId;
      rows.push({
        key: `${game.eventId}-${pick.entryId}-${pick.teamId}`,
        participation: "picked",
        entryId: pick.entryId,
        displayName: pick.displayName,
        isViewer: pick.isViewer,
        teamId: pick.teamId,
        teamName: teamNameFor(pick.teamId, teamsById),
        opponentName: teamNameFor(opponentId, teamsById),
        outcome: pick.outcome,
        outcomeLabel:
          survivorRoundPickResultLabel(pick.outcome) +
          (pick.decisionBasis === "straightUp"
            ? " · Straight up"
            : pick.decisionBasis === "spread"
              ? " · Spread"
              : pick.decisionBasis === null
                ? " · TBD"
                : ""),
      });
    }
  }
  return rows.sort((a, b) => {
    const byName = a.displayName.localeCompare(b.displayName, "en-US", {
      sensitivity: "base",
    });
    if (byName !== 0) return byName;
    return a.key.localeCompare(b.key);
  });
}

/**
 * The player filter's chips: one per entry with a row in this round, picked
 * or not. The viewer comes first as "You", then everyone else by name. The
 * search narrows the list without regard to case, but a picked player always
 * stays, so a fan can unpick them without clearing the search.
 */
export function buildMemberChoices(
  rows: readonly typeRoundPickRow[],
  input: { query: string; selectedEntryIds: readonly string[] },
): typeRoundPicksMemberChoice[] {
  const query = input.query.trim().toLowerCase();
  const selected = new Set(input.selectedEntryIds);
  const seen = new Map<string, { displayName: string; isViewer: boolean }>();
  for (const row of rows) {
    if (!seen.has(row.entryId)) {
      seen.set(row.entryId, {
        displayName: row.displayName,
        isViewer: row.isViewer,
      });
    }
  }
  return [...seen.entries()]
    .filter(
      ([entryId, person]) =>
        selected.has(entryId) ||
        query === "" ||
        person.displayName.toLowerCase().includes(query),
    )
    .sort(([, a], [, b]) => {
      if (a.isViewer !== b.isViewer) return a.isViewer ? -1 : 1;
      return a.displayName.localeCompare(b.displayName, "en-US", {
        sensitivity: "base",
      });
    })
    .map(([entryId, person]) => ({
      entryId,
      label: person.isViewer ? ROUND_PICKS_YOU_LABEL : person.displayName,
      isSelected: selected.has(entryId),
    }));
}

/** Picks a player in the filter, or unpicks one already picked. */
export function toggleMemberSelection(
  selectedEntryIds: readonly string[],
  entryId: string,
): string[] {
  return selectedEntryIds.includes(entryId)
    ? selectedEntryIds.filter((id) => id !== entryId)
    : [...selectedEntryIds, entryId];
}

/**
 * Narrows the rows by result and by player. An empty `entryIds` means
 * everyone. With nobody picked, a non-empty `memberQuery` narrows the rows by
 * name the way the mobile search does; once anyone is picked the search only
 * narrows the chips.
 */
export function filterRoundPickRows(
  rows: readonly typeRoundPickRow[],
  filters: {
    outcome: typeRoundPicksOutcomeFilter;
    entryIds: readonly string[];
    memberQuery?: string | undefined;
  },
): typeRoundPickRow[] {
  const selected = new Set(filters.entryIds);
  const query = (filters.memberQuery ?? "").trim().toLowerCase();
  return rows.filter((row) => {
    if (selected.size > 0) {
      if (!selected.has(row.entryId)) return false;
    } else if (query !== "" && !row.displayName.toLowerCase().includes(query)) {
      return false;
    }
    if (filters.outcome === "all") return true;
    if (row.participation === "picked") return row.outcome === filters.outcome;
    return row.participation === "missed" && filters.outcome === "loss";
  });
}
